import { LiveData, Service } from '@toeverything/infra';

import {
  AutomationConfigApiService,
  type WorkflowStepPreviewResponse,
  type WorkflowTestRunResponse,
} from './automation-config-api';

const WORKFLOW_TEST_RUN_AD_HOC_DOC_ID = '__workflow_builder_ad_hoc__';
const WORKFLOW_TEST_RUN_AD_HOC_BLOCK_ID = '__workflow_builder_ad_hoc__';

export class WorkflowTestContextService extends Service {
  readonly activeRun$ = new LiveData<WorkflowTestRunResponse | null>(null);

  constructor(private readonly configApi: AutomationConfigApiService) {
    super();
  }

  setActiveRun(run: WorkflowTestRunResponse | null): void {
    this.activeRun$.next(run);
  }

  getActiveRunForWorkflow(workflowId: string): WorkflowTestRunResponse | null {
    const run = this.activeRun$.value;
    return run?.run.workflowId === workflowId ? run : null;
  }

  async createFromAutomation(input: {
    workspaceId: string;
    docId: string;
    blockId: string;
  }): Promise<WorkflowTestRunResponse> {
    const run = await this.configApi.createWorkflowTestRun(
      input.blockId,
      input.workspaceId,
      input.docId
    );
    this.setActiveRun(run);
    return run;
  }

  async createFromAdHoc(input: {
    workspaceId: string;
    workflowId: string;
    inputData: Record<string, unknown>;
  }): Promise<WorkflowTestRunResponse> {
    const run = await this.configApi.createAdHocWorkflowTestRun(
      input.workspaceId,
      input.workflowId,
      input.inputData
    );
    this.setActiveRun(run);
    return run;
  }

  async loadLatestForAutomation(input: {
    workspaceId: string;
    blockId: string;
    workflowId?: string | null;
  }): Promise<WorkflowTestRunResponse | null> {
    const run = await this.configApi.getLatestWorkflowTestRun(
      input.blockId,
      input.workspaceId,
      input.workflowId
    );
    if (run) this.setActiveRun(run);
    return run;
  }

  async previewStep(input: {
    workspaceId: string;
    docId: string;
    blockId: string;
    testRunId: string;
    stepId: string;
  }): Promise<WorkflowStepPreviewResponse> {
    const activeRun = this.activeRun$.value;
    const isAdHocRun =
      activeRun?.run.id === input.testRunId &&
      activeRun.run.automationDocId === WORKFLOW_TEST_RUN_AD_HOC_DOC_ID &&
      activeRun.run.automationBlockId === WORKFLOW_TEST_RUN_AD_HOC_BLOCK_ID;

    const response = isAdHocRun
      ? await this.configApi.previewAdHocWorkflowStep(
          input.workspaceId,
          activeRun.run.workflowId,
          input.testRunId,
          input.stepId
        )
      : await this.configApi.previewWorkflowStep(
          input.blockId,
          input.workspaceId,
          input.docId,
          input.testRunId,
          input.stepId
        );
    this.setActiveRun({
      run: response.run,
      stepResults: response.stepResults,
    });
    return response;
  }
}
